/**
 * Geometria del carosello Trending Brands, senza DOM né React: la usano sia
 * il rendering iniziale lato server sia il controller nel browser.
 *
 * Le misure sono in `cqw`, cioè in centesimi della larghezza dello stage.
 * Lo slot 0 è la card centrale; ±1 e ±2 sono quelle ai lati. Fra uno slot e
 * l'altro dimensioni e posizione si interpolano in modo lineare: così una
 * card che si avvicina al centro cresce con continuità e il gap resta
 * costante in ogni istante del movimento.
 */

export type SlotGeometry = {
  /** Distanza del centro della card dal centro dello stage, con segno. */
  x: number
  width: number
  height: number
}

/** Spazio fra due card contigue. */
const GAP = 3.4

/** Centrale 4:3, laterali 3:4. */
const SLOT_SIZES = [
  { width: 40, height: 30 },
  { width: 19, height: 19 * (4 / 3) },
  { width: 16.5, height: 16.5 * (4 / 3) },
] as const

const LAST_SLOT = SLOT_SIZES.length - 1

/** Centro dello slot k (k >= 0), misurato dal centro dello stage. */
const SLOT_X = SLOT_SIZES.reduce<number[]>((positions, size, index) => {
  if (index === 0) {
    return [0]
  }
  const previous = SLOT_SIZES[index - 1]
  return [
    ...positions,
    positions[index - 1] + previous.width / 2 + GAP + size.width / 2,
  ]
}, [])

/** Altezza dello stage: quella della card centrale. */
export const STAGE_HEIGHT = SLOT_SIZES[0].height

/** Distanza fra il centro e lo slot ±1: un passo del carosello. */
export const STEP = SLOT_X[1]

/**
 * Oltre questo offset la card è fuori dallo stage: si nasconde, e il salto
 * da un lato all'altro dell'anello non si vede.
 */
export const VISIBLE_OFFSET = 2.5

/** Numero minimo di slot nell'anello: con meno, due copie si vedrebbero insieme. */
export const MIN_RING_SIZE = 6

const lerp = (from: number, to: number, t: number) => from + (to - from) * t

/** Geometria della card a `offset` posizioni dal centro (con segno). */
export function slotGeometry(offset: number): SlotGeometry {
  const distance = Math.abs(offset)
  const sign = offset < 0 ? -1 : 1

  if (distance >= LAST_SLOT) {
    const last = SLOT_SIZES[LAST_SLOT]
    const beyond = distance - LAST_SLOT
    return {
      x: sign * (SLOT_X[LAST_SLOT] + beyond * (last.width + GAP)),
      width: last.width,
      height: last.height,
    }
  }

  const from = Math.floor(distance)
  const t = distance - from
  const start = SLOT_SIZES[from]
  const end = SLOT_SIZES[from + 1]

  return {
    x: sign * lerp(SLOT_X[from], SLOT_X[from + 1], t),
    width: lerp(start.width, end.width, t),
    height: lerp(start.height, end.height, t),
  }
}

export function isVisibleOffset(offset: number): boolean {
  return Math.abs(offset) <= VISIBLE_OFFSET
}

/** Indice riportato nell'intervallo [0, size). */
export function wrapIndex(index: number, size: number): number {
  return ((index % size) + size) % size
}

/**
 * Offset con segno della card `index` rispetto al centro, nell'intervallo
 * [-size / 2, size / 2): `progress` non viene mai riavvolto, quindi i tween
 * restano continui anche dopo più giri.
 */
export function wrapOffset(
  index: number,
  progress: number,
  size: number
): number {
  return wrapIndex(index - progress + size / 2, size) - size / 2
}

/** Indice (nell'anello) della card che sta al centro. */
export function activeIndex(progress: number, size: number): number {
  return wrapIndex(Math.round(progress), size)
}

export type RingSlot<T> = {
  item: T
  /** Posizione dell'elemento nella lista originale. */
  itemIndex: number
  /** Le copie servono solo a riempire l'anello: non sono contenuto reale. */
  clone: boolean
}

/**
 * Anello di slot: la lista ripetuta fino ad avere almeno MIN_RING_SIZE
 * posti. Un solo elemento resta da solo (nessun movimento).
 */
export function buildRing<T>(items: T[]): RingSlot<T>[] {
  if (items.length <= 1) {
    return items.map((item, itemIndex) => ({ item, itemIndex, clone: false }))
  }

  const repeats = Math.ceil(MIN_RING_SIZE / items.length)
  return Array.from({ length: repeats * items.length }, (_, index) => ({
    item: items[index % items.length],
    itemIndex: index % items.length,
    clone: index >= items.length,
  }))
}
